/**
 * ClusterUtils - Funciones puras para clusters (sin dependencia de Leaflet)
 * V03 - Clustering de Marcadores
 *
 * PTEL Design System:
 * - §7 Scoring Visual: teal (1-10) → blue (11-50) → orange (51-100) → red (100+)
 */

/**
 * Tamaños de cluster según cantidad de elementos
 */
export type ClusterSize = 'sm' | 'md' | 'lg' | 'xl';

/**
 * Configuración de cada tamaño de cluster
 */
export interface ClusterSizeConfig {
  size: ClusterSize;
  className: string;
  iconSize: [number, number];
  color: string;
}

/**
 * Opciones de icono de cluster
 */
export interface ClusterIconOptions {
  /** Número de elementos en el cluster */
  count: number;
  /** Tipos de infraestructura en el cluster (opcional, para futuras mejoras) */
  types?: string[];
}

/**
 * Configuración de tamaños según rangos
 * - sm: 1-10 (teal)
 * - md: 11-50 (blue)
 * - lg: 51-100 (orange)
 * - xl: 100+ (red)
 */
export const CLUSTER_CONFIG: Record<ClusterSize, Omit<ClusterSizeConfig, 'size'>> = {
  sm: {
    className: 'cluster-sm',
    iconSize: [30, 30],
    color: '#2dd4bf', // teal
  },
  md: {
    className: 'cluster-md',
    iconSize: [40, 40],
    color: '#3b82f6', // blue
  },
  lg: {
    className: 'cluster-lg',
    iconSize: [50, 50],
    color: '#f97316', // orange
  },
  xl: {
    className: 'cluster-xl',
    iconSize: [60, 60],
    color: '#ef4444', // red
  },
};

/**
 * Determina el tamaño del cluster según la cantidad de elementos
 * @param count - Número de marcadores en el cluster
 * @returns Tamaño del cluster: 'sm' | 'md' | 'lg' | 'xl'
 */
export function getClusterSize(count: number): ClusterSize {
  if (count <= 10) return 'sm';
  if (count <= 50) return 'md';
  if (count <= 100) return 'lg';
  return 'xl';
}

/**
 * Obtiene el color del cluster según la cantidad de elementos
 * @param count - Número de marcadores en el cluster
 * @returns Color hexadecimal
 */
export function getClusterColor(count: number): string {
  const size = getClusterSize(count);
  return CLUSTER_CONFIG[size].color;
}

/**
 * Obtiene la configuración completa del cluster según la cantidad
 * @param count - Número de marcadores en el cluster
 * @returns Configuración del cluster
 */
export function getClusterConfig(count: number): ClusterSizeConfig {
  const size = getClusterSize(count);
  return {
    size,
    ...CLUSTER_CONFIG[size],
  };
}

/**
 * Formatea el número para mostrar en el cluster
 * - Números <= 999: se muestran tal cual
 * - Números > 999: se muestran como "1k+", "2k+", etc.
 * @param count - Número de marcadores
 * @returns String formateado
 */
export function formatClusterCount(count: number): string {
  if (count > 999) {
    return `${Math.floor(count / 1000)}k+`;
  }
  return String(count);
}

/**
 * Genera el HTML para un icono de cluster
 * @param count - Número de elementos
 * @returns HTML string para el icono
 */
export function generateClusterHtml(count: number): string {
  const config = getClusterConfig(count);
  const displayCount = formatClusterCount(count);
  return `<div class="ptel-cluster ${config.className}">${displayCount}</div>`;
}
